import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../context/LanguageContext";
import { SITE } from "../lib/constants";

/**
 * Landing SEO exact-match pour la requête "chirurgie de l'épaule Marseille"
 * URL: /chirurgie-epaule-marseille
 */
const ChirurgieEpauleMarseillePage = () => {
  const { lang } = useLanguage();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const isFr = lang === "fr";

  const title = isFr
    ? "Chirurgie de l'épaule à Marseille — Dr Richard Aswad | IMOTEpS 13008"
    : "Shoulder surgery in Marseille — Dr Richard Aswad | IMOTEpS 13008";
  const description = isFr
    ? "Chirurgie de l'épaule à Marseille (13008) par le Dr Richard Aswad — expert HAS. Coiffe des rotateurs, instabilité, prothèse anatomique et inversée, arthroscopie. Clinique Juge."
    : "Shoulder surgery in Marseille (13008) by Dr Richard Aswad — HAS expert. Rotator cuff, instability, anatomical and reverse prosthesis, arthroscopy. Clinique Juge.";

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://imoteps.doctor/" },
      { "@type": "ListItem", position: 2, name: "Chirurgie de l'épaule à Marseille", item: "https://imoteps.doctor/chirurgie-epaule-marseille" },
    ],
  };

  const medicalJsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: "Chirurgie de l'épaule à Marseille",
    bodyLocation: "Épaule",
    procedureType: "Surgical",
    description,
    performer: {
      "@type": "Physician",
      name: "Dr Richard Aswad",
      medicalSpecialty: "Orthopedic",
      url: "https://imoteps.doctor/",
      telephone: SITE.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: "118 Rue Jean Mermoz",
        postalCode: "13008",
        addressLocality: "Marseille",
        addressCountry: "FR",
      },
    },
    availableService: [
      { "@type": "MedicalProcedure", name: "Réparation arthroscopique de la coiffe des rotateurs" },
      { "@type": "MedicalProcedure", name: "Stabilisation arthroscopique (Bankart)" },
      { "@type": "MedicalProcedure", name: "Butée coracoïdienne (Latarjet)" },
      { "@type": "MedicalProcedure", name: "Prothèse anatomique d'épaule" },
      { "@type": "MedicalProcedure", name: "Prothèse inversée d'épaule" },
      { "@type": "MedicalProcedure", name: "Acromioplastie arthroscopique" },
    ],
  };

  const procedures = [
    { slug: "coiffe-rotateurs", name: "Réparation de la coiffe des rotateurs", desc: "Arthroscopie mini-invasive — 3 mini-incisions de 5 mm, ancres résorbables, montage en double rang quand l'anatomie le permet." },
    { slug: "instabilite-epaule", name: "Stabilisation pour luxation récidivante", desc: "Bankart arthroscopique chez le sujet jeune sans perte osseuse, butée coracoïdienne (Latarjet) chez le sportif de contact." },
    { slug: "prothese-epaule", name: "Prothèse anatomique ou inversée", desc: "Planification 3D pré-opératoire, choix anatomique vs inversée selon l'état de la coiffe. Expert HAS depuis 2018." },
    { slug: "tendinopathies-coiffe", name: "Acromioplastie & traitement du conflit", desc: "Décompression sous-acromiale arthroscopique, traitement des calcifications par arthroscopie ou ondes de choc." },
    { slug: "capsulite-retractile", name: "Arthrolyse pour capsulite", desc: "Mobilisation sous anesthésie ± arthrolyse arthroscopique pour les épaules gelées résistantes au traitement médical." },
    { slug: "disjonction-acromio-claviculaire", name: "Réparation acromio-claviculaire", desc: "Ligamentoplastie pour disjonctions de stade IV ou plus, par technique arthroscopique mini-invasive." },
  ];

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        canonical="/chirurgie-epaule-marseille"
        keywords="chirurgie de l'épaule Marseille, chirurgien épaule Marseille, opération épaule Marseille, chirurgie coiffe Marseille, prothèse épaule Marseille 13008, Bankart Marseille, Latarjet Marseille, Dr Aswad, IMOTEpS, Clinique Juge"
        lang={lang}
        type="article"
        jsonLd={medicalJsonLd}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />

      <main data-testid="chirurgie-epaule-marseille-page" className="bg-white pt-28 md:pt-36 pb-20">
        <article className="container-page max-w-5xl">
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-[#FF5A1F]">{isFr ? "Accueil" : "Home"}</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FF5A1F] font-semibold">{isFr ? "Chirurgie de l'épaule à Marseille" : "Shoulder surgery in Marseille"}</span>
          </nav>

          <header className="mt-6">
            <span className="chip">{isFr ? "Chirurgie spécialisée" : "Specialized surgery"}</span>
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
              {isFr
                ? "Chirurgie de l'épaule à Marseille — par un chirurgien expert HAS"
                : "Shoulder surgery in Marseille — by an HAS-recognized surgeon"}
            </h1>
            <p className="mt-5 text-lg text-foreground/80 leading-relaxed max-w-3xl">
              {isFr
                ? "Le Dr Richard Aswad pratique la chirurgie de l'épaule à Marseille (Clinique Juge — 13008) depuis plus de 25 ans. Plus de 8 000 interventions réalisées, expert HAS pour les prothèses d'épaule, formé à la Mayo Clinic (Rochester) et au Royal University Hospital (Toronto). Toutes les techniques modernes — arthroscopie, prothèse anatomique et inversée, stabilisation pour luxation récidivante, microchirurgie."
                : "Dr Richard Aswad has been practicing shoulder surgery in Marseille (Clinique Juge — 13008) for over 25 years. More than 8,000 interventions, HAS expert for shoulder prostheses, trained at the Mayo Clinic (Rochester) and the Royal University Hospital (Toronto). All modern techniques — arthroscopy, anatomical and reverse prostheses, instability stabilization, microsurgery."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/#contact" className="btn-primary" data-testid="cem-cta-contact">
                <i className="fa-solid fa-calendar-check" aria-hidden="true"></i>
                {isFr ? "Prendre rendez-vous" : "Book an appointment"}
              </Link>
              <a href={SITE.phoneHref} className="btn-ghost">
                <i className="fa-solid fa-phone" aria-hidden="true"></i>
                {SITE.phone}
              </a>
            </div>
          </header>

          <section className="mt-14">
            <h2 className="font-display text-3xl font-bold text-navy">
              {isFr ? "Pourquoi choisir un chirurgien spécialisé en chirurgie de l'épaule à Marseille ?" : "Why choose a shoulder specialist in Marseille?"}
            </h2>
            <p className="mt-4 text-base text-foreground/80 leading-relaxed">
              {isFr
                ? "L'épaule est l'articulation la plus mobile et la plus complexe du corps humain. Sa chirurgie demande une formation pointue, l'usage de techniques mini-invasives (arthroscopie, microchirurgie) et un volume opératoire suffisant pour entretenir la maîtrise. Le Dr Aswad consacre 100 % de sa pratique au membre supérieur, dont 60 % à l'épaule. Il opère exclusivement à la Clinique Juge — site reconnu pour ses blocs opératoires modernes (ISO 5, arthroscopie 4K) et son équipe d'anesthésistes spécialisés en bloc inter-scalénique."
                : "The shoulder is the most mobile and complex joint in the human body. Its surgery requires advanced training, minimally invasive techniques (arthroscopy, microsurgery) and sufficient surgical volume to maintain mastery. Dr Aswad dedicates 100% of his practice to the upper limb, of which 60% is shoulder surgery. He operates exclusively at Clinique Juge — recognized for its modern operating theaters (ISO 5, 4K arthroscopy) and specialized anesthesia team."}
            </p>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              <div className="rounded-xl border border-border p-5 bg-white">
                <p className="font-display text-3xl font-extrabold text-[#FF5A1F]">8 000+</p>
                <p className="mt-1 text-sm text-foreground/75">{isFr ? "Interventions réalisées" : "Surgeries performed"}</p>
              </div>
              <div className="rounded-xl border border-border p-5 bg-white">
                <p className="font-display text-3xl font-extrabold text-[#FF5A1F]">25+</p>
                <p className="mt-1 text-sm text-foreground/75">{isFr ? "Années d'expérience" : "Years of experience"}</p>
              </div>
              <div className="rounded-xl border border-border p-5 bg-white">
                <p className="font-display text-3xl font-extrabold text-[#FF5A1F]">{isFr ? "Expert HAS" : "HAS Expert"}</p>
                <p className="mt-1 text-sm text-foreground/75">{isFr ? "Prothèses d'épaule" : "Shoulder prostheses"}</p>
              </div>
            </div>
          </section>

          <section className="mt-14">
            <h2 className="font-display text-3xl font-bold text-navy">
              {isFr ? "Toutes les interventions de l'épaule" : "All shoulder procedures"}
            </h2>
            <p className="mt-3 text-base text-foreground/75 max-w-3xl">
              {isFr
                ? "Du traitement de la rupture de coiffe des rotateurs à la pose de prothèse, en passant par la stabilisation pour luxation récidivante : l'IMOTEpS couvre l'ensemble des pathologies chirurgicales de l'épaule."
                : "From rotator cuff repair to prosthesis implantation, including stabilization for recurrent dislocation: IMOTEpS covers the full range of surgical shoulder pathologies."}
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {procedures.map((p) => (
                <Link
                  key={p.slug}
                  to={`/pathologie/${p.slug}`}
                  data-testid={`cem-procedure-${p.slug}`}
                  className="group rounded-xl border border-border bg-white p-6 hover:border-[#FF5A1F]/40 hover:shadow-md transition-all"
                >
                  <p className="font-display font-bold text-navy text-lg group-hover:text-[#FF5A1F] transition-colors">
                    {p.name}
                  </p>
                  <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{p.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#FF5A1F]">
                    {isFr ? "En savoir plus" : "Learn more"}
                    <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-14 rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#102A4C] text-white p-8 md:p-12">
            <h2 className="font-display text-3xl font-bold">
              {isFr ? "Une approche personnalisée — du diagnostic à la reprise" : "A personalized approach — from diagnosis to recovery"}
            </h2>
            <p className="mt-4 text-white/85 max-w-3xl">
              {isFr
                ? "Chaque épaule est différente. Chaque patient l'est aussi. La consultation initiale comprend un examen clinique précis, une revue complète de l'imagerie (radiographies, échographie, IRM, scanner selon les cas), et un échange approfondi sur vos attentes : retour au sport, reprise du travail, soulagement de la douleur. Lorsque la chirurgie est indiquée, le Dr Aswad vous présente toutes les options — arthroscopique vs ouverte, ambulatoire vs hospitalisation courte — avec leurs avantages et leurs limites. La décision est partagée."
                : "Every shoulder is different. So is every patient. The initial consultation includes a precise clinical exam, a complete review of imaging (X-rays, ultrasound, MRI, CT scan as needed), and an in-depth discussion of your expectations: return to sport, return to work, pain relief. When surgery is indicated, Dr Aswad presents all options — arthroscopic vs open, outpatient vs short stay — with their benefits and limitations. Decision-making is shared."}
            </p>
          </section>

          <section className="mt-14">
            <h2 className="font-display text-3xl font-bold text-navy">
              {isFr ? "Cabinet & accès" : "Practice & access"}
            </h2>
            <div className="mt-5 grid md:grid-cols-2 gap-5">
              <div className="rounded-xl border border-border bg-white p-6">
                <h3 className="font-display font-bold text-navy text-xl">Clinique Juge</h3>
                <p className="mt-2 text-sm text-foreground/75">
                  118, rue Jean Mermoz<br />13008 Marseille
                </p>
                <p className="mt-3 text-xs text-muted-foreground">
                  {isFr
                    ? "Tramway T3 (arrêt Périer), bus 19, 21, 22, 41, 44, 521. Parking visiteurs sur place. Accessibilité PMR."
                    : "Tram T3, multiple buses. Visitor parking on-site. Accessibility for reduced mobility."}
                </p>
              </div>
              <div className="rounded-xl border border-border bg-white p-6">
                <h3 className="font-display font-bold text-navy text-xl">{isFr ? "Prendre rendez-vous" : "Book"}</h3>
                <p className="mt-2 text-sm text-foreground/75">
                  {isFr ? "Réservation directe sur Doctolib ou par téléphone. L'assistante Fabienne répond aux questions administratives." : "Direct booking on Doctolib or by phone. Fabienne, the assistant, answers administrative questions."}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a href={SITE.phoneHref} className="btn-primary !py-2 !px-4 text-xs">
                    <i className="fa-solid fa-phone" aria-hidden="true"></i>
                    {SITE.phone}
                  </a>
                  <a href={SITE.doctolibUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2 !px-4 text-xs">
                    <i className="fa-solid fa-calendar-plus" aria-hidden="true"></i>Doctolib
                  </a>
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>
    </>
  );
};

export default ChirurgieEpauleMarseillePage;
